import { Avatar } from "@dropbox/dig-components/avatar";
import { Text, Title } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { DashStackIcon } from "@mirage/mosaics/DCLWrappers/DashStackIcon";
import { getInitials } from "@mirage/shared/account";
import { getDefaultColorIndex } from "@mirage/stacks/themes";
import i18n from "@mirage/translations";
import { useMemo } from "react";

import { Skeleton } from "@dropbox/dig-components/skeleton";
import styles from "./StackChatHeader.module.css";

export type StackChatHeaderData = {
  colorIndex?: number;
  creatorDisplayName?: string;
  creatorProfileImgUrl?: string;
  customColor?: string;
  emoji?: string;
  memberCount: number;
  name: string;
  iconUrl?: string; // Optional URL for the stack icon
};

type StackChatHeaderProps = {
  data: StackChatHeaderData | null;
  isLoading: boolean;
};

export function StackChatHeader({ data, isLoading }: StackChatHeaderProps) {
  const creatorInitials = data?.creatorDisplayName
    ? getInitials(data.creatorDisplayName)
    : "";

  const memberCount = useMemo(() => {
    if (!data || data?.memberCount === 0) {
      return i18n.t("no_members");
    }

    return i18n.t("num_members", { count: data.memberCount });
  }, [data?.memberCount]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        width="100%"
      >
        <Box>
          <Skeleton.Box className={styles.skeletonStackEmoji} />
        </Box>
        <Box>
          <Skeleton.Box className={styles.stackName} height={28} />
        </Box>
        <Box alignItems="center" display="flex" width="100%">
          <Skeleton.Avatar className={styles.creatorAvatar} size="xsmall" />
          <Skeleton.Text size="medium" width={330} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
      width="100%"
    >
      <Box>
        <DashStackIcon
          aria-label={i18n.t("aria_emoji_button")}
          colorIndex={data?.colorIndex || getDefaultColorIndex()}
          customColor={data?.customColor}
          emoji={data?.emoji}
          iconUrl={data?.iconUrl}
          size={"xlarge"}
        />
      </Box>
      <Box>
        <Title className={styles.stackName} weightVariant="emphasized">
          {data?.name}
        </Title>
      </Box>
      <Box alignItems="center" display="flex">
        <Avatar
          className={styles.creatorAvatar}
          size="xsmall"
          src={data?.creatorProfileImgUrl}
        >
          {creatorInitials}
        </Avatar>
        <Box className={styles.stackHeaderMetadata} display="inline-block">
          <Text color="subtle" size="medium" variant="label">
            {i18n.t("created_by", { name: data?.creatorDisplayName })}
          </Text>
          <Text color="subtle" size="medium" variant="label">
            {memberCount}
          </Text>
        </Box>
      </Box>
    </Box>
  );
}
